import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBackIos, Visibility } from '@mui/icons-material';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import parse from 'html-react-parser';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Tooltip } from '@mui/material';
import { Header } from '../../components/header/header';
import { CreateNoteBar } from '../../templates/notes/createNoteBar/createNoteBar';
import {
  styledCheckboxHeader,
  styledColumnHeaderCheckbox,
  ContainerStyled
} from '../../components/layout/main/mainStyles';
import { RightSideBar } from '../../components/layout/rightSideBar/rightSideBar';
import { viewRightSideBarEnum } from '../../constants/sidebarEnums';
import { ButtonIconStyled, TitleStyled } from '../devices/devicesStyles';
import Strings from '../../i18n';
import {
  useGetNotesByDevicesMutation,
  useGetNotesByLocationsMutation,
  useGetNotesByUsersMutation,
  useGetNotesMutation
} from '../../services/notes/notesService';
import { INoteItemList } from '../../types/notes/notesInterface';
import {
  ContainerTable,
  StyledButtonName
} from '../../templates/locations/locationsList/locationListStyles';
import { SingleDetailsNote } from '../../templates/notes/singleDetailsNote/singleDetailsNote';
import { ViewAndEditNote } from '../../components/commons/notes/viewAndEditNote/viewAndEditNote';
import { NotesEnum } from '../../constants/notes';
import { TooltipStyled } from '../locations/locationStyles';

export const MyNotesList = () => {
  const navigate = useNavigate();
  const [openSideBar, setOpenSideBar] = useState(false);
  const [textToShow, setTextToShow] = useState('');
  const [titleToShow, setTitleToShow] = useState('');
  const [visible, setVisible] = useState(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openSideFarFor, setOpenSidebarFor] = useState(
    viewRightSideBarEnum.empty
  );
  const [selectedNotes, setSelectedNotes] = useState<INoteItemList[]>([]);

  const [getNotes, { data }] = useGetNotesMutation();
  const [getNotesByDevices, { data: deviceNotes }] =
    useGetNotesByDevicesMutation();
  const [getNotesByLocations, { data: locationNotes }] =
    useGetNotesByLocationsMutation();
  const [getNotesByUsers, { data: userNotes }] = useGetNotesByUsersMutation();

  const location = useLocation();
  const { type, id } = location.state || {};

  useEffect(() => {
    if (type && id) {
      switch (type) {
        case NotesEnum.Device:
          getNotesByDevices(id);
          return;
        case NotesEnum.Location:
          getNotesByLocations(id);
          return;
        case NotesEnum.Users:
          getNotesByUsers(id);
          return;
        default:
          getNotes();
      }
    } else {
      getNotes();
    }
  }, [type, id]);

  const reloadTable = () => {
    if (type && id) {
      switch (type) {
        case NotesEnum.Device:
          getNotesByDevices(id);
          return;
        case NotesEnum.Location:
          getNotesByLocations(id);
          return;
        case NotesEnum.Users:
          getNotesByUsers(id);
          return;
        default:
          getNotes();
      }
    } else {
      getNotes();
    }
    setOpenSideBar(false);
    setSelectedNotes([]);
  };
  const redirectToLocation = (locationId: number) => {
    navigate(`/locations/${locationId}/devices`);
  };
  const redirectToUser = (userId: number) => {
    navigate(`/users?searchUser=${userId}`);
  };
  const redirectToDevice = (deviceId: number) => {
    navigate(`/server-access/devices?search=${deviceId}`);
  };

  const closeSideBar = () => {
    setOpenSideBar(false);
  };
  const getHeaderCheckbox = () => {
    return (
      <Checkbox
        className="p-checkbox"
        style={{
          visibility: selectedNotes.length ? 'visible' : 'hidden',
          ...styledCheckboxHeader
        }}
        checked={(selectedNotes && selectedNotes.length > 0) ?? false}
        onChange={() => {
          setSelectedNotes([]);
        }}
      />
    );
  };

  const getObjectId = (): {
    deviceId?: string;
    locationId?: string;
    userId?: string;
  } | null => {
    switch (type) {
      case NotesEnum.Device:
        return { deviceId: id };
      case NotesEnum.Location:
        return { locationId: id };
      case NotesEnum.Users:
        return { userId: id };
      default:
        return null;
    }
  };

  const viewText = (rowData: INoteItemList) => {
    return <span>{rowData.text.replace(/<[^>]*>/g, '')}</span>;
  };
  const switchComponentsToShowRightSideBar = () => {
    switch (openSideFarFor) {
      case viewRightSideBarEnum.locationDetailsBusinessAccount:
        return '';
      case viewRightSideBarEnum.noteDetails:
        return (
          <SingleDetailsNote
            closeSideBar={closeSideBar}
            selectedNote={selectedNotes[0]}
            reloadTable={reloadTable}
            openSideBarForMethod={() => {
              setOpenEditModal(true);
              closeSideBar();
            }}
            openToViewNote={(selected: INoteItemList) => {
              setTextToShow(selected.text);
              setTitleToShow(selected.targetName);
              setVisible(true);
              closeSideBar();
            }}
          />
        );
      case viewRightSideBarEnum.editNote:
        return '';

      case viewRightSideBarEnum.createNote:
        return (
          <CreateNoteBar
            type={type}
            id={getObjectId()}
            closeSideBar={closeSideBar}
            reloadTable={reloadTable}
          />
        );
      default:
        return '';
    }
  };

  useEffect(() => {
    if (selectedNotes.length === 1) {
      setOpenSidebarFor(viewRightSideBarEnum.noteDetails);

      setOpenSideBar(true);
    } else if (selectedNotes.length > 1) {
      setOpenSideBar(false);
    } else {
      setOpenSidebarFor(viewRightSideBarEnum.empty);
      setOpenSideBar(false);
    }
  }, [selectedNotes]);

  const getDate = (rowData: INoteItemList) => {
    const date = new Date(rowData.date);
    return <span>{date.toLocaleString()}</span>;
  };

  const getTitle = () => {
    switch (type) {
      case 'Devices':
        return deviceNotes?.[0]?.targetName ?? '';
      case 'Locations':
        return locationNotes?.[0]?.targetName ?? '';
      case 'Users':
        return userNotes?.[0]?.targetName ?? '';
      default:
        return '';
    }
  };

  return (
    <>
      <Header>
        <ContainerStyled>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <ButtonIconStyled size="small" onClick={() => navigate(-1)}>
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <TitleStyled>
                {data
                  ? `${Strings.notes.myNotes}`
                  : `${Strings.notes.notesFor} ${getTitle()}`}
              </TitleStyled>
            </div>
            <div className="col-8 col-sm-4 text-right">
              <Tooltip
                title={Strings.notes.createNote}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={() => {
                    setOpenSidebarFor(viewRightSideBarEnum.createNote);
                    setOpenSideBar(true);
                  }}
                >
                  <AddCircleOutlineIcon />
                </ButtonIconStyled>
              </Tooltip>{' '}
            </div>
          </div>
        </ContainerStyled>
      </Header>
      <ContainerTable component="main">
        {(data || deviceNotes || locationNotes || userNotes) && (
          <DataTable
            selection={selectedNotes}
            selectionMode="multiple"
            metaKeySelection
            onSelectionChange={(e) => {
              setSelectedNotes(e.value);
            }}
            value={data || deviceNotes || locationNotes || userNotes}
            size="small"
            scrollable
            scrollHeight="flex"
            paginator={data && data.length >= 50}
            selectionPageOnly
            rows={50}
            rowsPerPageOptions={[5, 10, 25, data ? data.length : 50]}
            tableStyle={{ fontSize: '14px' }}
          >
            <Column
              selectionMode="multiple"
              headerStyle={styledColumnHeaderCheckbox}
              header={getHeaderCheckbox}
              frozen
            />
            <Column field="date" header="Date" sortable body={getDate} />
            <Column field="" header="Note" body={viewText} />
          </DataTable>
        )}
      </ContainerTable>
      <RightSideBar open={openSideBar} onMenuClose={() => closeSideBar()}>
        {switchComponentsToShowRightSideBar()}
      </RightSideBar>
      <Dialog
        header={titleToShow}
        visible={visible}
        onHide={() => {
          setVisible(false);
        }}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        {parse(textToShow)}
      </Dialog>
      <ViewAndEditNote
        note={selectedNotes[0]}
        onDelete={() => {}}
        onCancel={() => {
          setSelectedNotes([]);
          setOpenEditModal(false);
        }}
        open={openEditModal}
        setOpen={setOpenEditModal}
        reloadTable={() => {
          getNotes();
        }}
      />
    </>
  );
};
